import services from 'service/axios';

import { CATALOGS_PATH, CATALOGS_PATH_V2 } from 'config/api.routes';

import type { GenericResponse } from 'commons/modules/generic-response';
import type {
  CatalogDefinitionResponse,
  CatalogDetailResponse,
  CatalogRequest,
  CatalogResponse,
  CatalogsResponse,
} from 'commons/modules/service/catalogsV2';

import { handleResponse } from 'utils/responseTransformer';
import { removeEmptyParams } from 'utils/services';

export const getCatalogsEbr = async (): Promise<
  GenericResponse<CatalogDetailResponse>
> => {
  try {
    const res = await services.wt_api(`${CATALOGS_PATH_V2}/catalogs/ebr`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};

export const getCatalogByCatalogKey = async (
  catalogKey: string,
): Promise<GenericResponse<CatalogsResponse>> => {
  const params = {
    catalogKey,
  };

  try {
    const res = await services.wt_api(`${CATALOGS_PATH}/values`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      params,
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};

export const getCatalogByParams = async (params: {
  catalogKey: string;
  page?: number;
  pageSize?: number;
  code01?: string;
  code02?: string;
  code03?: string;
  code04?: string;
  code05?: string;
  sortBy?: string;
  sort?: string;
}): Promise<GenericResponse<CatalogsResponse>> => {
  removeEmptyParams(params);

  try {
    const res = await services.wt_api(`${CATALOGS_PATH}/values`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      params: {
        ...params,
        //  sortBy: params.sortBy || 'sequenceId',
        //   sort: params.sort || 'asc',
      },
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};

export const addCatalog = async (
  body: CatalogRequest,
): Promise<GenericResponse<CatalogResponse>> => {
  try {
    const res = await services.wt_api(`${CATALOGS_PATH}/save`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(body),
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'Ocurrio un error al guardar el catálogo',
    );
  }
};

export const updateCatalog = async (
  body: CatalogRequest,
): Promise<GenericResponse<CatalogResponse>> => {
  try {
    const res = await services.wt_api(
      `${CATALOGS_PATH}/${body.catalogId}/${body.sequenceId}`,
      {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        data: JSON.stringify(body),
      },
    );
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};

export const updateCatalogs = async (
  body: CatalogRequest[],
): Promise<GenericResponse<CatalogsResponse>> => {
  try {
    const res = await services.wt_api(`${CATALOGS_PATH}/batch`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(body),
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'Error al intentar guardar la informaci\u00F3n',
    );
  }
};

export const deleteCatalog = async (
  catalogId: number,
  sequenceId: number,
): Promise<GenericResponse<any>> => {
  const params = {
    status: 'A',
  };
  try {
    const res = await services.wt_api(
      `${CATALOGS_PATH}/${catalogId}/${sequenceId}`,
      {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        params,
      },
    );
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};

export const getCatalogDetailsByCatalogCodes = async (
  catalogCodes: string,
): Promise<GenericResponse<CatalogDetailResponse>> => {
  const params = {
    catalogCodes,
  };
  try {
    const res = await services.wt_api(`${CATALOGS_PATH_V2}/catalogs/details`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      params,
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};

export const getRegulatoryCatalogsService = async (
  catalogs: string,
): Promise<GenericResponse<CatalogsResponse>> => {
  try {
    const res = await services.wt_api(
      `${CATALOGS_PATH}/regulatory/values?catalogKey=${catalogs}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};

export const getCatalogService = async (
  catalogKey: string,
  marketID: number,
  groupID: string | undefined,
  page = 0,
  pageSize = 1000,
): Promise<GenericResponse<CatalogsResponse>> => {
  const query = `?key01=${marketID}`
    .concat(groupID ? `&key02=${groupID}` : '')
    .concat(`&catalogKey=${catalogKey}`)
    .concat(`&page=${page}&pageSize=${pageSize}`);

  try {
    const res = await services.wt_api(`${CATALOGS_PATH}/values${query}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'Ocurri\u00F3 un error',
    );
  }
};

export const getCatalogDefinitionById = async (
  catalogKey: string,
): Promise<GenericResponse<CatalogDefinitionResponse>> => {
  try {
    const res = await services.wt_api(
      `${CATALOGS_PATH_V2}/definitions/${catalogKey}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );
    const { data, status } = res;
    return handleResponse(data, status, true);
  } catch (error: any) {
    return handleResponse(
      null,
      error?.response?.status,
      false,
      error,
      null,
      'No se pudieron obtener los catálogos',
    );
  }
};
